import React from 'react'
import styled from 'styled-components'
import { UserPlus, Search, Flame, Crown } from 'lucide-react'
import {
  StaggerContainer,
  StaggerItem,
  staggerContainer,
  smoothEase,
} from './animations/AnimatedSection'

const stepVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: smoothEase },
  },
}

const JourneyStepsSection = () => {
  const steps = [
    {
      number: '01',
      icon: <UserPlus size={24} color="#2A2A2A" strokeWidth={2} />,
      title: 'Join the Community',
      desc: 'Sign up and become part of a global network of scientists, educators, students, and changemakers.',
      hasDash: true,
    },
    {
      number: '02',
      icon: <Search size={24} color="#2A2A2A" strokeWidth={2} />,
      title: 'Discover Your Guardian',
      desc: 'Take our SDG quiz to find which Sustainable Development Goal aligns with your expertise and passion.',
      hasDash: true,
    },
    {
      number: '03',
      icon: <Flame size={24} color="#2A2A2A" strokeWidth={2} />,
      title: 'Take Meaningful Action',
      desc: 'Participate in research-backed challenges, collaborative projects, and community initiatives.',
      hasDash: true,
    },
    {
      number: '04',
      icon: <Crown size={24} color="#2A2A2A" strokeWidth={2} />,
      title: 'Community',
      desc: 'Connect with guardians worldwide',
      hasDash: false,
    },
  ]

  return (
    <Section>
      <StepsGrid variants={staggerContainer}>
        {steps.map((step, idx) => (
          <StaggerItem key={idx} variants={stepVariants}>
            <StepColumn>
              <NumberContainer>
                <StepNumber>{step.number}</StepNumber>
                {step.hasDash && <DashedLine />}
              </NumberContainer>
              <CardContent>
                <IconContainer>{step.icon}</IconContainer>
                <TextGroup>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDesc>{step.desc}</StepDesc>
                </TextGroup>
              </CardContent>
            </StepColumn>
          </StaggerItem>
        ))}
      </StepsGrid>
    </Section>
  )
}

const StepsGrid = styled(StaggerContainer)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 64px;
  width: 100%;
  max-width: 1440px;

  @media (max-width: 1024px) {
    flex-direction: column;
    align-items: center;
    gap: 48px;
  }
`

const Section = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 24px;
  width: 100%;
  background-color: #ffffff;

  @media (max-width: 768px) {
    padding: 48px 24px 80px;
  }
`

const StepColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex: 1;
  width: 100%;
  max-width: 280px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`

const NumberContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const StepNumber = styled.div`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  letter-spacing: -0.04em;
  color: #919191;
  background: #ffffff;
  padding: 0 8px;
  position: relative;
  z-index: 1;
`

const DashedLine = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(100% + 64px);
  height: 0px;
  border-top: 1px dashed #dedede;
  z-index: 0;

  @media (max-width: 768px) {
    display: none;
  }
`

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  height: 100%;
`

const IconContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 48px;
  height: 48px;
  background-color: #bbe148;
  border-width: 1px 3px 3px 1px;
  border-style: solid;
  border-color: #2a2a2a;
  border-radius: 16px;
  flex-shrink: 0;
`

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
`

const StepTitle = styled.h4`
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  text-align: center;
  letter-spacing: -0.04em;
  color: #2a2a2a;
  margin: 0;
`

const StepDesc = styled.p`
  font-family: 'Inter', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: #5e5e5e;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 20px;
  }
`

export default JourneyStepsSection
